/**
 * Activation badge body — nav-item host (DC-23 "nav item + popover"; AC-040).
 * A chip that opens a Popover with the SAME short form; never a Dialog
 * (modal depth stays 0 — the badge is a quick peek, not a task the user
 * commits to). Router-free: mounted by ActivationChecklist's `badge` variant.
 */
import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import { LAUNCH_ACTIVATION_TEST_IDS } from '../testIds';
import { SHORT_FORM_COPY } from '../copy/shortFormCopy';
import { ActivationShortForm } from './ActivationShortForm';
import type { ActivationBodyProps } from './ActivationShortForm';
import { CHIP_FOCUS_SX } from './chipFocusSx';

/** Task 3 interfaces (brief §Interfaces) referred to this name; same shape as `ActivationBodyProps`. */
export type ActivationBadgeProps = ActivationBodyProps;

export const ActivationBadgeBody: React.FC<ActivationBodyProps> = ({ projection, onNavigate, openDialog, onRefresh, questionnaireUrl }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>): void => {
    // The chip sits inside a RouterLink menu item — never let the click navigate.
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (): void => setAnchorEl(null);
  const navigateAndClose = (path: string): void => {
    handleClose();
    onNavigate(path);
  };
  /**
   * A class-D action opens its real dialog over the current page (follow-up F1) — the
   * peek popover must get out of the way first, or the dialog stacks on top of it.
   * `false` = the host does not own that dialog kind, so `runActivationAction` falls back
   * to a route and `navigateAndClose` closes the popover instead.
   */
  const openDialogAndClose = openDialog
    ? (dialog: Parameters<NonNullable<typeof openDialog>>[0], props?: Record<string, string>): boolean | void => {
        const handled = openDialog(dialog, props);
        if (handled !== false) handleClose();
        return handled;
      }
    : undefined;

  return (
    <>
      <Chip
        size="small"
        variant="outlined"
        color={projection.complete ? 'success' : 'warning'}
        label={projection.complete ? SHORT_FORM_COPY.chipComplete : SHORT_FORM_COPY.chip(projection.earnedCount, projection.totalCount)}
        onClick={handleOpen}
        aria-haspopup="true"
        aria-expanded={open}
        data-testid={LAUNCH_ACTIVATION_TEST_IDS.badge}
        sx={CHIP_FOCUS_SX}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 320 }, 'data-testid': LAUNCH_ACTIVATION_TEST_IDS.popover } as Record<string, unknown> }}
      >
        {/*
          The Popover is a PORTAL, but React events still bubble along the component tree —
          the badge lives inside a RouterLink menu item, so without this guard every click
          inside the popover (an action button included) ALSO fires the menu item's link and
          navigates to /launch, defeating the whole point of opening a dialog in place (F1).
          The chip itself stops its own click in `handleOpen`; this covers the popover body.
        */}
        <Box onClick={(event) => event.stopPropagation()}>
          <ActivationShortForm
            projection={projection}
            onNavigate={navigateAndClose}
            openDialog={openDialogAndClose}
            onRefresh={onRefresh}
            questionnaireUrl={questionnaireUrl}
          />
        </Box>
      </Popover>
    </>
  );
};
